import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export function InquiryDialog({ open, onOpenChange, property, room, offer }) {
  const [form, setForm] = useState({
    guest_name: "", guest_email: "", guest_phone: "",
    check_in: "", check_out: "", guests: 2, special_requests: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/inquiries", {
        property_id: property.id,
        room_id: room?.id || null,
        offer_id: offer?.id || null,
        ...form,
        guests: Number(form.guests),
      });
      setReference(data.reference);
      toast.success("Request submitted — our team will confirm shortly.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-md border border-[var(--aur-border)] bg-[var(--aur-bg)] px-3 py-2.5 font-sans text-sm text-[var(--aur-text)] placeholder:text-[var(--aur-text-2)] focus:border-gold focus:outline-none";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-[var(--aur-border)] bg-[var(--aur-card)] text-[var(--aur-text)]">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Request to Book</DialogTitle>
          <DialogDescription className="font-sans text-sm text-[var(--aur-text-2)]">
            Submit your preferences — our reservations team confirms availability by hand. No payment is taken now.
          </DialogDescription>
        </DialogHeader>

        {reference ? (
          <div className="py-6 text-center" data-testid="inquiry-confirmation">
            <p className="eyebrow">Request received</p>
            <p className="mt-4 font-serif text-2xl">Thank you.</p>
            <p className="mt-3 font-sans text-sm text-[var(--aur-text-2)]">
              Your reference is <span className="font-mono text-gold">{reference}</span>. Our reservations
              team will manually confirm availability and email you shortly.
            </p>
            <button
              onClick={() => onOpenChange(false)}
              className="mt-6 rounded-full bg-gold px-6 py-2.5 font-sans text-xs font-semibold uppercase tracking-widest text-[var(--aur-bg)]"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <p className="font-sans text-sm text-[var(--aur-text-2)]">
              {property?.name}{room ? ` · ${room.name}` : ""}{offer ? ` · ${offer.title}` : ""}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <input required placeholder="Full name" value={form.guest_name} onChange={set("guest_name")} className={inputCls} data-testid="inquiry-name" />
              <input required type="email" placeholder="Email" value={form.guest_email} onChange={set("guest_email")} className={inputCls} data-testid="inquiry-email" />
            </div>
            <input placeholder="Phone (optional)" value={form.guest_phone} onChange={set("guest_phone")} className={inputCls} data-testid="inquiry-phone" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="eyebrow mb-1 block">Check-in</label>
                <input required type="date" value={form.check_in} onChange={set("check_in")} className={inputCls} data-testid="inquiry-checkin" />
              </div>
              <div>
                <label className="eyebrow mb-1 block">Check-out</label>
                <input required type="date" value={form.check_out} onChange={set("check_out")} className={inputCls} data-testid="inquiry-checkout" />
              </div>
            </div>
            <div>
              <label className="eyebrow mb-1 block">Guests</label>
              <input type="number" min="1" max="12" value={form.guests} onChange={set("guests")} className={inputCls} data-testid="inquiry-guests" />
            </div>
            <textarea placeholder="Special requests" value={form.special_requests} onChange={set("special_requests")} rows={3} className={inputCls} data-testid="inquiry-requests" />
            <button
              type="submit"
              disabled={submitting}
              data-testid="inquiry-form-submit"
              className="w-full rounded-full bg-gold px-6 py-3 font-sans text-xs font-semibold uppercase tracking-widest text-[var(--aur-bg)] transition-all hover:bg-gold-bright disabled:opacity-60"
            >
              {submitting ? "Submitting…" : "Submit Request"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
